import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'

import { useApi } from '@/app/api'

import { failureOf, isApiError } from './errors'

import type { components } from './index'

type Schemas = components['schemas']

export type Profile = Schemas['Profile']
export type ProfileRequest = Schemas['ProfileRequest']
export type Country = Schemas['Country']
export type ResidenceOption = Schemas['ResidenceOption']
export type Document = Schemas['Document']
export type DocumentField = Schemas['DocumentField']
export type Analysis = Schemas['Analysis']
export type Dashboard = Schemas['Dashboard']
export type DashboardTask = Schemas['DashboardTask']
export type CategoryStatus = Schemas['CategoryStatus']
export type Roadmap = Schemas['Roadmap']
export type Evaluation = Schemas['Evaluation']
export type Task = Schemas['Task']
export type Subscription = Schemas['Subscription']
export type Answer = Schemas['Answer']
export type Conversation = Schemas['Conversation']
export type SurveySummary = Schemas['SurveySummary']
export type SurveyState = Schemas['SurveyState']
export type SurveyQuestion = Schemas['SurveyQuestion']
export type SurveyChoice = Schemas['SurveyChoice']
export type SurveyPipeline = Schemas['SurveyPipeline']
export type SurveyPipelineResult = Schemas['SurveyPipelineResult']

export const keys = {
  profile: ['profile'] as const,
  countries: ['reference', 'countries'] as const,
  residenceTypes: (country: string) => ['reference', 'residence-types', country] as const,
  dashboard: ['dashboard'] as const,
  documents: ['documents'] as const,
  roadmap: ['roadmap'] as const,
  tasks: ['tasks'] as const,
  subscription: ['subscription'] as const,
  conversation: (id: string) => ['chat', id] as const,
  surveys: ['surveys'] as const,
  // A root of its own rather than ['surveys', tag]: invalidating the list would
  // otherwise match the state of the run by prefix and refetch the answer that
  // has just come back, costing a round trip and a flash of the old question.
  survey: (tag: string) => ['survey', tag] as const,
}

async function unwrap<T>(call: Promise<{ data?: T; error?: unknown }>): Promise<T> {
  const { data, error } = await call

  if (error) {
    throw failureOf(error)
  }

  return data as T
}

export function useProfile(): UseQueryResult<Profile> {
  const api = useApi()

  return useQuery({
    queryKey: keys.profile,
    queryFn: ({ signal }) => unwrap(api.GET('/profile', { signal })),
    retry: false,
  })
}

export function useCountries(): UseQueryResult<Country[]> {
  const api = useApi()

  return useQuery({
    queryKey: keys.countries,
    queryFn: ({ signal }) => unwrap(api.GET('/reference/countries', { signal })),
    staleTime: Infinity,
  })
}

export function useResidenceTypes(country: string): UseQueryResult<ResidenceOption[]> {
  const api = useApi()

  return useQuery({
    queryKey: keys.residenceTypes(country),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/reference/residence-types', { params: { query: { country } }, signal })),
    enabled: country.length === 2,
    staleTime: Infinity,
  })
}

export function useDashboard(): UseQueryResult<Dashboard> {
  const api = useApi()

  return useQuery({
    queryKey: keys.dashboard,
    queryFn: ({ signal }) => unwrap(api.GET('/dashboard', { signal })),
    retry: false,
  })
}

export function useDocuments(): UseQueryResult<Document[]> {
  const api = useApi()

  return useQuery({
    queryKey: keys.documents,
    queryFn: async ({ signal }) => (await unwrap(api.GET('/documents', { signal }))).items,
  })
}

export function useRoadmap(): UseQueryResult<Roadmap> {
  const api = useApi()

  return useQuery({
    queryKey: keys.roadmap,
    queryFn: ({ signal }) => unwrap(api.GET('/roadmap', { signal })),
    retry: false,
  })
}

export function useTasks(): UseQueryResult<Task[]> {
  const api = useApi()

  return useQuery({
    queryKey: keys.tasks,
    queryFn: async ({ signal }) => (await unwrap(api.GET('/tasks', { signal }))).items,
  })
}

export function useSubscription(): UseQueryResult<Subscription> {
  const api = useApi()

  return useQuery({
    queryKey: keys.subscription,
    queryFn: ({ signal }) => unwrap(api.GET('/subscription', { signal })),
  })
}

export function useConversation(id: string | undefined): UseQueryResult<Conversation> {
  const api = useApi()

  return useQuery({
    queryKey: keys.conversation(id ?? ''),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/chat/{id}', { params: { path: { id: id ?? '' } }, signal })),
    enabled: Boolean(id),
  })
}

export function useSaveProfile(
  existing: boolean,
): UseMutationResult<Profile, Error, ProfileRequest> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: (body: ProfileRequest) =>
      existing ? unwrap(api.PATCH('/profile', { body })) : unwrap(api.POST('/profile', { body })),
    onSuccess: (profile) => {
      client.setQueryData(keys.profile, profile)
      void client.invalidateQueries({ queryKey: keys.dashboard })
      void client.invalidateQueries({ queryKey: keys.roadmap })
      void client.invalidateQueries({ queryKey: keys.tasks })
    },
  })
}

export interface Upload {
  type: string
  file: File
}

// useEvaluateRoadmap recomputes what the country asks of this person and
// returns the roadmap it produced. Onboarding waits on it: the screen that says
// «working out your deadlines» is showing this call, not a timer.
export function useEvaluateRoadmap(): UseMutationResult<Roadmap, Error, void> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => unwrap(api.POST('/roadmap/evaluate')),
    onSuccess: (roadmap) => {
      client.setQueryData(keys.roadmap, roadmap)
      void client.invalidateQueries({ queryKey: keys.dashboard })
      void client.invalidateQueries({ queryKey: keys.tasks })
    },
  })
}

export function useUploadDocument(): UseMutationResult<Analysis, Error, Upload> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ type, file }: Upload) => {
      const body = new FormData()
      body.append('type', type)
      body.append('file', file)

      return unwrap(
        api.POST('/documents', {
          body: body as unknown as Schemas['DocumentRequest'],
          bodySerializer: (form: unknown) => form as FormData,
        }),
      ) as Promise<Analysis>
    },
    onSuccess: () => invalidateDocuments(client),
  })
}

export function useDeleteDocument(): UseMutationResult<void, Error, string> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await unwrap(api.DELETE('/documents/{id}', { params: { path: { id } } }))
    },
    onSuccess: () => invalidateDocuments(client),
  })
}

export interface FieldConfirmation {
  documentId: string
  key: string
  value: string
}

export function useConfirmField(): UseMutationResult<Document, Error, FieldConfirmation> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, key, value }: FieldConfirmation) =>
      unwrap(
        api.PATCH('/documents/{id}/fields/{key}', {
          params: { path: { id: documentId, key } },
          body: { value },
        }),
      ),
    onSuccess: () => invalidateDocuments(client),
  })
}

export function useAsk(): UseMutationResult<
  Answer,
  Error,
  { question: string; conversationId?: string }
> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ question, conversationId }: { question: string; conversationId?: string }) =>
      unwrap(
        api.POST('/chat', {
          body: conversationId ? { question, conversation_id: conversationId } : { question },
        }),
      ),
    onSuccess: (answer) => {
      void client.invalidateQueries({ queryKey: keys.conversation(answer.conversation_id) })
    },
  })
}

export function useEraseAccount(): UseMutationResult<void, Error, void> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await unwrap(api.DELETE('/account'))
    },
    onSuccess: () => client.clear(),
  })
}

// useSetPlan, useClearDocuments and useResetProfile back the development panel
// of the settings screen. Their endpoints exist only when the API runs with
// APP_ENV=development; anywhere else the call fails as a plain 404, which is
// the honest answer.
export function useSetPlan(): UseMutationResult<void, Error, 'free' | 'premium'> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (plan: 'free' | 'premium') => {
      await unwrap(api.PUT('/dev/plan', { body: { plan } }))
    },
    onSuccess: () => invalidateDocuments(client),
  })
}

export function useClearDocuments(): UseMutationResult<number, Error, void> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const cleared = await unwrap(api.DELETE('/dev/documents'))

      return cleared.deleted
    },
    onSuccess: () => invalidateDocuments(client),
  })
}

// useResetProfile takes the account back to where onboarding starts. The
// profile query is removed rather than invalidated: refetching it would only
// ask for something that no longer exists and store the 404 as an error, while
// the caller is on its way to the wizard anyway.
export function useResetProfile(): UseMutationResult<number, Error, void> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const reset = await unwrap(api.DELETE('/dev/profile'))

      return reset.erased
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: keys.profile })
      void client.invalidateQueries({ queryKey: keys.dashboard })
      void client.invalidateQueries({ queryKey: keys.roadmap })
      void client.invalidateQueries({ queryKey: keys.tasks })
    },
  })
}

export function useSurveys(): UseQueryResult<SurveySummary[]> {
  const api = useApi()

  return useQuery({
    queryKey: keys.surveys,
    queryFn: ({ signal }) => unwrap(api.GET('/surveys', { signal })),
    retry: false,
  })
}

export function useSurvey(tag: string): UseQueryResult<SurveyState> {
  const api = useApi()

  return useQuery({
    queryKey: keys.survey(tag),
    queryFn: ({ signal }) =>
      unwrapState(api.GET('/surveys/{tag}', { params: { path: { tag } }, signal })),
    enabled: tag.length > 0,
    retry: false,
  })
}

export interface SurveyAnswer {
  tag: string
  question: string
  value: unknown
}

export function useAnswerQuestion(): UseMutationResult<SurveyState, Error, SurveyAnswer> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ tag, question, value }: SurveyAnswer) =>
      unwrapState(
        api.POST('/surveys/{tag}/answers', {
          params: { path: { tag } },
          body: { question, value },
        }),
      ),
    onSuccess: (state) => settle(client, state),
  })
}

export interface SurveySkip {
  tag: string
  question: string
}

export function useSkipQuestion(): UseMutationResult<SurveyState, Error, SurveySkip> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ tag, question }: SurveySkip) =>
      unwrapState(
        api.POST('/surveys/{tag}/skip', { params: { path: { tag } }, body: { question } }),
      ),
    onSuccess: (state) => settle(client, state),
  })
}

export function useSurveyBack(): UseMutationResult<SurveyState, Error, string> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: (tag: string) =>
      unwrapState(api.POST('/surveys/{tag}/back', { params: { path: { tag } } })),
    onSuccess: (state) => settle(client, state),
  })
}

// usePauseSurvey is «save and leave». The answers were already stored one by
// one; what this call adds is the recount of the roadmap, so everything the
// rules feed is invalidated behind it.
export function usePauseSurvey(): UseMutationResult<SurveyState, Error, string> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: (tag: string) =>
      unwrapState(api.POST('/surveys/{tag}/pause', { params: { path: { tag } } })),
    onSuccess: (state) => {
      settle(client, state)
      invalidateRules(client)
    },
  })
}

export interface PipelineRun {
  tag: string
  pipeline: string
  file: File
}

export function useRunPipeline(): UseMutationResult<SurveyPipelineResult, Error, PipelineRun> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ tag, pipeline, file }: PipelineRun) => {
      const body = new FormData()
      body.append('file', file)

      return unwrap(
        api.POST('/surveys/{tag}/pipelines/{pipeline}', {
          params: { path: { tag, pipeline } },
          body: body as unknown as { file: string },
          bodySerializer: (form: unknown) => form as FormData,
        }),
      )
    },
    onSuccess: (result) => {
      settle(client, result.state)
      invalidateDocuments(client)
    },
  })
}

export function useRestartSurvey(): UseMutationResult<SurveyState, Error, string> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: (tag: string) =>
      unwrapState(api.POST('/surveys/{tag}/restart', { params: { path: { tag } } })),
    onSuccess: (state) => settle(client, state),
  })
}

function settle(client: ReturnType<typeof useQueryClient>, state: SurveyState): void {
  client.setQueryData(keys.survey(state.tag), state)
  void client.invalidateQueries({ queryKey: keys.surveys })

  if (state.status === 'completed') {
    void client.invalidateQueries({ queryKey: keys.profile })
    invalidateRules(client)
  }
}

function invalidateRules(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: keys.dashboard })
  void client.invalidateQueries({ queryKey: keys.roadmap })
  void client.invalidateQueries({ queryKey: keys.tasks })
}

// unwrapState keeps the state a refusal carries. Answering a question the
// person has already moved past — two tabs, a resent tap — is a 409 whose body
// is the question they are actually on, and redrawing from it is the whole
// point of sending it (§3.11 of docs/plan/survey.md).
async function unwrapState(
  call: Promise<{ data?: SurveyState; error?: unknown }>,
): Promise<SurveyState> {
  try {
    return await unwrap(call)
  } catch (cause) {
    const state = stateOf(cause)

    if (state) {
      return state
    }

    throw cause
  }
}

function stateOf(cause: unknown): SurveyState | undefined {
  if (!isApiError(cause) || cause.status !== 409) {
    return undefined
  }

  const body = cause.body

  return body && typeof body === 'object' && 'progress' in body ? (body as SurveyState) : undefined
}

function invalidateDocuments(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: keys.documents })
  void client.invalidateQueries({ queryKey: keys.dashboard })
  void client.invalidateQueries({ queryKey: keys.roadmap })
  void client.invalidateQueries({ queryKey: keys.tasks })
  void client.invalidateQueries({ queryKey: keys.subscription })
}
